import React, { KeyboardEvent, MouseEvent, PropsWithChildren, useContext } from 'react'
import clsx from 'clsx'
import { AnchorNav as AnchorNavStyles } from '@airbus/elements/elements'
import { createUseStyles } from '../../lib/styling'
import { AnchorNavContext } from './AnchorNav'
import { CommonProps, LinkTarget } from '../helperTypes'

const useStyles = createUseStyles(AnchorNavStyles, 'anchornav')

type Props = PropsWithChildren<{
  /** Disabled state */
  disabled?: boolean
  /** optional href url */
  href?: string
  /** handler dispatched when the item is selected */
  onSelected?: (href?: string) => void
  /**
   * Selected state<br />
   * Set `disableAutoUpdate` prop on `AnchorNav` to manage this manually.
   **/
  selected?: boolean
  /**
   * optional href target
   * @default _self
   */
  target?: LinkTarget
}>

export type AnchorNavListItemProps = Props & CommonProps

const AnchorNavListItem = React.forwardRef<HTMLLIElement, AnchorNavListItemProps>(
  (
    { children, className, disabled = false, href, onSelected, selected = false, style, target = '_self', ...rest },
    ref,
  ) => {
    const classes = useStyles()
    const { selectedHash } = useContext(AnchorNavContext)

    const dispatchSelect = (evt: KeyboardEvent | MouseEvent, stopPropagation: boolean = false) => {
      if (stopPropagation) {
        evt.stopPropagation()
        return
      }
      if (onSelected) {
        evt.preventDefault()
        onSelected(href)
      }
    }

    return (
      <li
        ref={ref}
        className={clsx(
          className,
          classes.item,
          disabled && classes.item_disabled,
          (selected || selectedHash === href) && classes.item_selected,
        )}
        style={style}
      >
        <a
          className={classes.itemLink}
          href={href || '#'}
          tabIndex={disabled ? -1 : undefined}
          target={target}
          onKeyDown={(evt: KeyboardEvent) => {
            if (evt.key === ' ' || evt.key === 'Enter') dispatchSelect(evt, disabled)
          }}
          onClick={(evt: MouseEvent) => dispatchSelect(evt, disabled)}
          {...rest}
        >
          {children}
        </a>
      </li>
    )
  },
)

export default AnchorNavListItem
